import { useMemo } from 'react';
import { Button, Flex, Portal, Skeleton, Text } from '@mantine/core';
import { DetailsPagination } from '@dealstack/frontend/shared/components/DetailsPagination/DetailsPagination.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useInvestorDetailsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/useInvestorDetailsController.ts';
import {
	InvestorDetailsColumns,
	SKELETON_OVERLAY,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/InvestorDetailsV2.styles.tsx';
import { StyledIcon } from '@dealstack/frontend/shared/layout/DashboardGrid/styled';
import { InvestorStatusBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorStatusBadge.tsx';
import { CopyIconButton } from '@dealstack/frontend/shared/components/CopyIconButton/CopyIconButton.tsx';
import { InvestorCohorts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorCohorts.tsx';
import { InviteInvestorButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InviteInvestorButton.tsx';
import { ProgressBar } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/ProgressBar/ProgressBar.tsx';
import { InvestorProfileCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorProfileCard/InvestorProfileCard.tsx';
import { CustomFieldsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldsCard.tsx';
import { AddressesCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AddressesCard/AddressesCard.tsx';
import { TaxRecordsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/TaxRecordsCard/TaxRecordsCard.tsx';
import { BankAccountsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/BankAccountsCard/BankAccountsCard.tsx';
import { InvestmentVehiclesCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestmentVehiclesCard/InvestmentVehiclesCard.tsx';
import { HoldingsSummaryCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsSummaryCard/HoldingsSummaryCard.tsx';
import { HoldingsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCard.tsx';
import { LoansCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/LoansCard/LoansCard.tsx';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { DocumentsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/DocumentsCard.tsx';
import { ActiveJourneysCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/ActiveJourneysCard.tsx';
import { AccountActivityCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AccountActivityCard/AccountActivityCard.tsx';
import { RecentActivityCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/RecentActivityCard/RecentActivityCard.tsx';
import { QuickLinksCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/QuickLinksCard/QuickLinksCard.tsx';
import { EmploymentRecordsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/EmploymentRecordsCard/EmploymentRecordsCard.tsx';
import { InvestorInvitationStatus } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

export const InvestorDetailsV2 = () => {
	const tMipManage = useTranslationMipManage();
	const {
		investor,
		investorId,
		dealId,
		mipId,
		isLoading,
		isError,
		isDetailsColumnCollapsed,
		toggleDetailsColumn,
		openMessages,
		pagination,
		refreshInvestor,
	} = useInvestorDetailsController();

	const isNotInvited =
		investor?.investorInvitation?.status === InvestorInvitationStatus.NOT_INVITED;

	const documentPersonIds = useMemo(
		() =>
			[investor?.person.id, ...(investor?.pivPersons ?? []).map((piv) => piv.id)].filter(
				(id): id is string => !!id
			),
		[investor]
	);

	const renderHeaderAction = () => {
		if (isLoading) {
			return <Skeleton height={40} width={170} radius="sm" />;
		}

		if (isNotInvited) {
			return <InviteInvestorButton investorId={investorId} onClose={refreshInvestor} />;
		}

		return (
			<Button
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VIEW_MESSAGES_BUTTON}
				size="md"
				variant="default"
				onClick={openMessages}
				leftSection={<Icon variant="mail" size="lg" />}
			>
				{tMipManage('investors.view_messages')}
			</Button>
		);
	};

	const pageTitle = (
		<Flex align="baseline" gap="0.5rem">
			<StyledIcon
				dataTestId={TEST_ELEMENT_IDS.TOGGLE_PANEL_ICON}
				variant={isDetailsColumnCollapsed ? 'expand-left-panel' : 'collapse-left-panel'}
				size="xl"
				onClick={toggleDetailsColumn}
			/>

			<Flex direction="column">
				<Flex gap="0.75rem" align="center" pos="relative">
					{isLoading ? (
						<>
							<Text fz={32} fw={600} style={{ visibility: 'hidden' }}>
								&nbsp;
							</Text>

							<Skeleton
								height={32}
								width={320}
								radius="sm"
								style={SKELETON_OVERLAY}
							/>
						</>
					) : (
						<>
							<Text
								fz={32}
								fw={600}
								c="dark.8"
								data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_NAME}
							>
								{investor?.person.displayName}
							</Text>

							<InvestorStatusBadge
								invitationStatus={investor?.investorInvitation?.status}
								programmeStatus={investor?.programmeStatus}
							/>
						</>
					)}
				</Flex>

				<Flex gap="0.5rem" align="center" pos="relative">
					{isLoading ? (
						<>
							<Text fz={14} fw={600} style={{ visibility: 'hidden' }}>
								&nbsp;
							</Text>

							<Skeleton
								height={16}
								width={260}
								radius="sm"
								style={SKELETON_OVERLAY}
							/>
						</>
					) : (
						<>
							<Flex gap="0.25rem" align="center">
								<Text
									c="dark.8"
									fz={14}
									fw={600}
									data-testid={
										TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_EMAIL
									}
								>
									{investor?.person.email}
								</Text>

								{investor?.person.email && (
									<CopyIconButton
										value={investor.person.email}
										dataTestId={
											TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_COPY_EMAIL_BUTTON
										}
									/>
								)}
							</Flex>

							<InvestorCohorts
								investorId={investorId}
								investorName={investor?.person.displayName ?? ''}
							/>
						</>
					)}
				</Flex>
			</Flex>
		</Flex>
	);

	return (
		<PageAbsoluteContainer>
			<Portal target="#mip-manage-header-slot">
				{pagination.currentIndex >= 0 && (
					<DetailsPagination
						label={tMipManage('investors_pagination.label')}
						counter={tMipManage('details_pagination.counter', {
							current: pagination.currentIndex + 1,
							total: pagination.total,
						})}
						prevDisabled={pagination.prevDisabled}
						nextDisabled={pagination.nextDisabled}
						onPrev={pagination.goPrev}
						onNext={pagination.goNext}
					/>
				)}
			</Portal>

			<PageHeader customTitle={pageTitle} hideBreadcrumbs>
				<Flex direction="column" justify="flex-end">
					{renderHeaderAction()}
				</Flex>
			</PageHeader>

			<InvestorDetailsColumns $detailsCollapsed={isDetailsColumnCollapsed}>
				<aside className="column details">
					<div className="column-content">
						<InvestorProfileCard
							dealId={dealId}
							investor={investor}
							isLoading={isLoading}
						/>

						<CustomFieldsCard
							investorId={investorId}
							investor={investor}
							isLoading={isLoading}
							onSaved={refreshInvestor}
						/>

						<EmploymentRecordsCard personId={investor?.person.id} />

						<AddressesCard addresses={investor?.person.addresses} />

						<TaxRecordsCard personId={investor?.person.id} />

						<BankAccountsCard personId={investor?.person.id} />

						<InvestmentVehiclesCard
							dealId={dealId}
							mipId={mipId}
							vehicles={investor?.pivPersons}
							isLoading={isLoading}
							isError={isError}
						/>
					</div>
				</aside>

				<section className="column main">
					<div className="column-content">
						<HoldingsSummaryCard investorId={investorId} />

						<HoldingsCard dealId={dealId} investorId={investorId} />

						<LoansCard dealId={dealId} investorId={investorId} />

						<DocumentsCard
							viewStorageKey={StorageKeys.MIP_INVESTOR_DETAILS_DOCUMENTS_VIEW}
							dealId={dealId}
							mipId={mipId}
							personIds={documentPersonIds}
							isInvestorLoading={isLoading}
							isInvestorError={isError}
						/>

						<QuickLinksCard
							dealId={dealId}
							mipId={mipId}
							investorId={investorId}
							isInvestorLoading={isLoading}
						/>
					</div>
				</section>

				<aside className="column activity">
					<div className="column-content">
						<ActiveJourneysCard dealId={dealId} mipId={mipId} investorId={investorId} />

						<RecentActivityCard
							dealId={dealId}
							personId={investor?.person.id}
							isLoading={isLoading}
						/>

						<AccountActivityCard
							investorId={investorId}
							invitationStatus={investor?.investorInvitation?.status}
							latestSession={investor?.latestSession}
							isLoading={isLoading}
							onInvited={refreshInvestor}
						/>
					</div>
				</aside>
			</InvestorDetailsColumns>

			<ProgressBar refreshInvestors={refreshInvestor} />
		</PageAbsoluteContainer>
	);
};
